import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import { Select as SELECT } from "src/components/api/wishlist_master/wishlistMaster";

type WishlistItem = {
  WishListMasterID: number;
  ProductMasterID: number;
  ProductName: string;
  Price: number;
  AddedDate: string;
  StockStatus: string;
  UserID: number;
  ImagePaths: string[];
  DOC: string;
};

type WishlistState = {
  /** Number of distinct product lines in the wishlist. */
  lineCount: number;
  items: WishlistItem[];
};

const initialState: WishlistState = {
  lineCount: 0,
  items: [],
};

function syncCountsFromItems(state: WishlistState) {
  state.lineCount = state.items.length;
}

export const fetchWishlist = createAsyncThunk(
  'wishlist/fetchWishlist',
  async () => {
    const response = await SELECT();
    if (!response?.status) {
      throw new Error('Failed to fetch wishlist');
    }
    const parsed = JSON.parse(response?.data!);
    return { 
      items: parsed.Data || [],
      count: parsed.WishListProductCount || 0
    };
  }
);

const wishlistSlice = createSlice({
  name: 'wishlist',
  initialState,
  reducers: {
    addWishlistItem: (state, action: PayloadAction<WishlistItem>) => {
      const incoming = action.payload;
      const existing = state.items.find(
        (item) => item.ProductMasterID === incoming.ProductMasterID,
      );
      if (!existing) {
        state.items.push(incoming);
      }
      syncCountsFromItems(state);
    },
    setWishlistItems: (state, action: PayloadAction<WishlistItem[]>) => {
      state.items = action.payload;
      syncCountsFromItems(state);
    },
    removeWishlistItem: (state, action: PayloadAction<number>) => {
      state.items = state.items.filter(
        (item) => item.ProductMasterID !== action.payload,
      );
      syncCountsFromItems(state);
    },
    clearWishlistItems: (state) => {
      state.items = [];
      state.lineCount = 0;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchWishlist.fulfilled, (state, action) => {
        state.items = action.payload.items;
        state.lineCount = action.payload.count;
      })
      .addCase(fetchWishlist.rejected, (state) => {
        state.lineCount = 0;
        state.items = [];
      });
  },
});

export const {
  addWishlistItem,
  setWishlistItems,
  removeWishlistItem,
  clearWishlistItems,
} = wishlistSlice.actions;

export const selectWishlistCount = (state: { wishlist: WishlistState }) =>
  state.wishlist.lineCount;

export default wishlistSlice.reducer;
